import { Dispatch, SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import { deleteMarketingOverviews, UserKeys } from '../../api/users-api';
import { MarketingOverviewModel } from '../../../types';
import { handleErrors } from '../../utils/errors';
import SharedDialog from '../../shared/shared-dialog';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteMarketingBarsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
}

export default function DeleteMarketingBarsDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
}: DeleteMarketingBarsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteMarketingOverviews(selectedRows),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The marketing bar has been deleted.'
            : 'The marketing bars have been deleted.',
      });

      queryClient.setQueryData(
        [UserKeys.MarketingOverviewModels],
        (oldData: MarketingOverviewModel[]) =>
          oldData.filter((sig) => !selectedRows.includes(sig.id))
      );

      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserSettings],
      });

      setSelectedRows([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue deleting the marketing bar. Please refresh and try again.'
            : 'There was an issue deleting the marketin bars. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.gpanel_view.marketing_manager.delete_marketing_bar,
        {
          module: ModuleList.modules.gpanelView.name,
          section: ModuleList.modules.gpanelView.sections.marketingManager,
        }
      );
    },
  });

  function handleSubmit() {
    mutate();
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Marketing Bar"
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            {selectedRows.length > 1
              ? `Do you wish to delete the selected ${selectedRows.length} marketing bars?`
              : selectedRows.length
              ? `Do you wish to delete the marketing bar?`
              : ''}
          </Typography>
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
